/**
 * Output plan: turns a result table into what the custom function returns
 * (the formula-cell value) plus the static cells written around/after it.
 * Pure; spec §5.3 rule 4b.
 */

export interface Cell {
  v: string | number | boolean | null; // null = truly empty
  date?: boolean;
  /** A date with a time of day (tick timestamps): written with the date-time format. Implies date. */
  datetime?: boolean;
  /** A time of day (a fraction of a day, CF_TIME): written with the format h:mm:ss AM/PM. */
  time?: boolean;
  /** A real Excel error, not text: v is its literal ("#N/A", golden w_rh_null_na_blanks). */
  error?: boolean;
  /** Placeholder for the formula cell inside a written block: the writer must not touch it. */
  skip?: boolean;
}

export interface Table {
  cells: Cell[][];
  /** Top-left is an empty header corner (row-header and column-header axes both present). */
  cornerEmpty: boolean;
  /**
   * An error's text, which LSEG shows in the formula cell whatever the Dest: nothing is
   * written at Dest (golden interp_opt_error_dest).
   */
  inFormulaCell?: boolean;
}

export interface OutputPlan {
  formulaValue: string | number | boolean;
  formulaIsDate: boolean;
  /** The formula cell holds a date-time (format m/d/yyyy h:mm). */
  formulaIsDateTime?: boolean;
  /** The formula cell holds a time of day (format h:mm:ss AM/PM). */
  formulaIsTime?: boolean;
  /** The formula cell is a real #N/A error (the function returns an error, not text). */
  formulaIsError?: boolean;
  /** 0-based coords on the formula's own sheet; top-left of the written block. */
  writes: { anchor: { row: number; col: number }; cells: Cell[][] } | null;
  /** true → formulaValue is the "not enough rows/columns" text and writes is null. */
  overflow: boolean;
}

export const NOT_ENOUGH_ROOM = "There are not enough rows/columns available to display the results.";

/** A Dest on another worksheet: shown in the formula cell, nothing written (spec §5.3 rule 3). */
export const DEST_OTHER_SHEET = "The destination cell and the function cell must be on the same worksheet.";

const MAX_ROWS = 1048576;
const MAX_COLS = 16384;

const pad2 = (n: number): string => String(n).padStart(2, "0");

export function updatedAt(now: Date): string {
  return `Updated at ${pad2(now.getHours())}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())}`;
}

const emptyCell = (): Cell => ({ v: null });
const skipCell = (): Cell => ({ v: null, skip: true });

/** The table minus its top-left cell: a rectangle whose first cell is a skip placeholder (the formula cell). */
function withoutTopLeft(cells: Cell[][]): Cell[][] {
  return cells.map((row, r) => (r === 0 ? [skipCell(), ...row.slice(1)] : row));
}

/** A cell; `sheet` absent means the formula's own sheet. */
export interface At {
  sheet?: string;
  row: number;
  col: number;
}

/** A Dest naming another worksheet than the formula's (sheet names compare case-insensitively, as in Excel). */
export function destOnOtherSheet(formula: At, dest: At | null): boolean {
  return (
    dest !== null &&
    dest.sheet !== undefined &&
    formula.sheet !== undefined &&
    dest.sheet.toLowerCase() !== formula.sheet.toLowerCase()
  );
}

export function planOutput(
  table: Table,
  formula: At,
  dest: At | null,
  sheetSize: { rows: number; cols: number } = { rows: MAX_ROWS, cols: MAX_COLS },
): OutputPlan {
  const cells = table.cells;
  const height = cells.length;
  const width = height ? Math.max(...cells.map((r) => r.length)) : 0;
  // A Dest on another worksheet is refused: LSEG writes nothing anywhere
  // (golden sideload_retest_lseg_vs_eav, L!I13 with Dest 'L2'!B2).
  if (destOnOtherSheet(formula, dest)) {
    return { formulaValue: DEST_OTHER_SHEET, formulaIsDate: false, writes: null, overflow: false };
  }
  const toDest = !table.inFormulaCell && dest !== null && (dest.row !== formula.row || dest.col !== formula.col);
  const from = toDest ? dest! : formula;
  const anchor = { row: from.row, col: from.col };

  // Rule 1: must fit from its anchor.
  if (anchor.row + height > sheetSize.rows || anchor.col + width > sheetSize.cols) {
    return { formulaValue: NOT_ENOUGH_ROOM, formulaIsDate: false, writes: null, overflow: true };
  }

  // Rule 2: Dest elsewhere — whole table at Dest, status in the formula cell.
  if (toDest) {
    return {
      formulaValue: updatedAt(new Date()),
      formulaIsDate: false,
      writes: { anchor: { ...anchor }, cells },
      overflow: false,
    };
  }

  // Rule 3: empty corner — status in the formula cell, rest of the table around it.
  if (table.cornerEmpty) {
    return {
      formulaValue: updatedAt(new Date()),
      formulaIsDate: false,
      writes: { anchor: { ...anchor }, cells: withoutTopLeft(cells) },
      overflow: false,
    };
  }

  // Rule 4: the formula cell holds the top-left value.
  const top = cells[0]?.[0] ?? emptyCell();
  const single = height === 1 && width === 1;
  return {
    formulaValue: top.v ?? "",
    formulaIsDate: !!top.date,
    ...(top.datetime ? { formulaIsDateTime: true } : {}),
    ...(top.time ? { formulaIsTime: true } : {}),
    ...(top.error ? { formulaIsError: true } : {}),
    writes: single ? null : { anchor: { ...anchor }, cells: withoutTopLeft(cells) },
    overflow: false,
  };
}
